/**
 * Analytics tracking service
 * Centralized analytics event logging with typed event names
 */
import { getAnalytics, logEvent as firebaseLogEvent, setUserId as firebaseSetUserId } from 'firebase/analytics';
import { app } from './firebase';

// Initialize Analytics (only in browser environment)
let analytics = null;
if (typeof window !== 'undefined') {
  try {
    analytics = getAnalytics(app);
  } catch (error) {
    console.error('Failed to initialize analytics:', error);
  }
}

// ============================================
// User Identification
// ============================================

/**
 * Set the user ID for analytics tracking
 * Call this when user logs in to associate all events with the user
 * @param {string|null} userId - Firebase UID or null to clear
 */
export const setAnalyticsUserId = (userId) => {
  if (analytics) {
    try {
      firebaseSetUserId(analytics, userId);
    } catch (error) {
      console.error('Analytics setUserId error:', error);
    }
  }
};

/**
 * Clear the user ID (call on logout)
 */
export const clearAnalyticsUserId = () => {
  setAnalyticsUserId(null);
};

/**
 * Analytics Event Names
 * Use these constants instead of raw strings
 * All events are prefixed with "offboard_" for easy filtering
 */
export const AnalyticsEvents = {
  // Authentication events
  USER_LOGIN: 'offboard_user_login',
  USER_LOGOUT: 'offboard_user_logout',
  
  // OTA Management events
  OTA_PAGE_VIEW: 'offboard_ota_page_view',
  OTA_CHANNEL_SELECTED: 'offboard_ota_channel_selected',
  OTA_FLASH_STARTED: 'offboard_ota_flash_started',
  OTA_FLASH_COMPLETED: 'offboard_ota_flash_completed',
  OTA_FLASH_FAILED: 'offboard_ota_flash_failed',
  
  // Device events
  DEVICE_CONNECTED: 'offboard_device_connected',
  DEVICE_DISCONNECTED: 'offboard_device_disconnected',
  DEVICE_UNSUPPORTED: 'offboard_device_unsupported',
};

/**
 * Base log event function
 * @param {string} eventName - Name of the event from AnalyticsEvents
 * @param {object} eventParams - Optional parameters for the event
 */
const logEvent = (eventName, eventParams = {}) => {
  if (analytics) {
    try {
      firebaseLogEvent(analytics, eventName, eventParams);
    } catch (error) {
      console.error('Analytics error:', error);
    }
  }
};

// ============================================
// Authentication Events
// ============================================

/**
 * Log user login event
 * @param {string} method - Login method (google, google_redirect)
 * @param {string} emailDomain - Domain of user's email
 */
export const trackUserLogin = (method, emailDomain) => {
  logEvent(AnalyticsEvents.USER_LOGIN, {
    method,
    email_domain: emailDomain || 'unknown'
  });
};

/**
 * Log user logout event
 */
export const trackUserLogout = () => {
  logEvent(AnalyticsEvents.USER_LOGOUT);
};

// ============================================
// OTA Management Events
// ============================================

/**
 * Log OTA page view
 */
export const trackOTAPageView = () => {
  logEvent(AnalyticsEvents.OTA_PAGE_VIEW, {
    page: 'ota_management'
  });
};

/**
 * Log OTA channel selection
 * @param {string} channel - Selected channel (dev, beta, production)
 */
export const trackOTAChannelSelected = (channel) => {
  logEvent(AnalyticsEvents.OTA_CHANNEL_SELECTED, {
    channel
  });
};

/**
 * Log OTA flash started
 * @param {object} params - Flash parameters
 * @param {string} params.channel - Firmware channel
 * @param {string} params.firmwareVersion - Version being flashed
 * @param {string} params.deviceMac - Device MAC address
 */
export const trackOTAFlashStarted = ({ channel, firmwareVersion, deviceMac }) => {
  logEvent(AnalyticsEvents.OTA_FLASH_STARTED, {
    channel,
    firmware_version: firmwareVersion,
    device_mac: deviceMac || 'unknown'
  });
};

/**
 * Log OTA flash completed successfully
 * @param {object} params - Flash parameters
 * @param {string} params.channel - Firmware channel
 * @param {string} params.firmwareVersion - Version that was flashed
 * @param {string} params.deviceMac - Device MAC address
 */
export const trackOTAFlashCompleted = ({ channel, firmwareVersion, deviceMac }) => {
  logEvent(AnalyticsEvents.OTA_FLASH_COMPLETED, {
    channel,
    firmware_version: firmwareVersion,
    device_mac: deviceMac || 'unknown'
  });
};

/**
 * Log OTA flash failed
 * @param {object} params - Flash parameters
 * @param {string} params.channel - Firmware channel
 * @param {string} params.firmwareVersion - Version that failed to flash
 * @param {string} params.deviceMac - Device MAC address
 * @param {string} params.error - Error message
 */
export const trackOTAFlashFailed = ({ channel, firmwareVersion, deviceMac, error }) => {
  logEvent(AnalyticsEvents.OTA_FLASH_FAILED, {
    channel,
    firmware_version: firmwareVersion,
    device_mac: deviceMac || 'unknown',
    error: error || 'unknown'
  });
};

// ============================================
// Device Events
// ============================================

/**
 * Log device connected
 * @param {object} params - Device parameters
 * @param {string} params.chipType - Type of chip (ESP32-S3, etc.)
 * @param {string} params.deviceMac - Device MAC address
 */
export const trackDeviceConnected = ({ chipType, deviceMac }) => {
  logEvent(AnalyticsEvents.DEVICE_CONNECTED, {
    chip_type: chipType,
    device_mac: deviceMac || 'unknown'
  });
};

/**
 * Log device disconnected
 * @param {string} deviceMac - Device MAC address
 */
export const trackDeviceDisconnected = (deviceMac) => {
  logEvent(AnalyticsEvents.DEVICE_DISCONNECTED, {
    device_mac: deviceMac || 'unknown'
  });
};

/**
 * Log unsupported device connected
 * @param {object} params - Device parameters
 * @param {string} params.chipType - Type of chip that was detected
 * @param {string} params.deviceMac - Device MAC address
 */
export const trackDeviceUnsupported = ({ chipType, deviceMac }) => {
  logEvent(AnalyticsEvents.DEVICE_UNSUPPORTED, {
    chip_type: chipType || 'unknown',
    device_mac: deviceMac || 'unknown'
  });
};

const analyticsService = {
  AnalyticsEvents,
  setAnalyticsUserId,
  clearAnalyticsUserId,
  trackUserLogin,
  trackUserLogout,
  trackOTAPageView,
  trackOTAChannelSelected,
  trackOTAFlashStarted,
  trackOTAFlashCompleted,
  trackOTAFlashFailed,
  trackDeviceConnected,
  trackDeviceDisconnected,
  trackDeviceUnsupported,
};

export default analyticsService;
